import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CASE_001, characterById } from '../case/case001';
import { COURT_EVENT_SCRIPTS, getCourtEventBeat } from '../game/courtEvents';
import { useGame } from '../game/GameContext';
import type { CharacterId, GamePhase } from '../game/types';

class ProceduralScore {
  readonly context: AudioContext;
  private readonly master: GainNode;
  private readonly music: GainNode;
  private readonly sfx: GainNode;
  private readonly filter: BiquadFilterNode;
  private readonly drones: OscillatorNode[] = [];
  private noiseSequence = 0;

  constructor() {
    this.context = new AudioContext();
    this.master = this.context.createGain();
    this.music = this.context.createGain();
    this.sfx = this.context.createGain();
    this.filter = this.context.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.value = 520;
    this.music.connect(this.filter).connect(this.master);
    this.sfx.connect(this.master);
    this.master.connect(this.context.destination);

    [55, 82.41, 110].forEach((frequency, index) => {
      const oscillator = this.context.createOscillator();
      const gain = this.context.createGain();
      oscillator.type = index === 0 ? 'sine' : 'triangle';
      oscillator.frequency.value = frequency;
      gain.gain.value = index === 0 ? 0.12 : 0.045;
      oscillator.connect(gain).connect(this.music);
      oscillator.start();
      this.drones.push(oscillator);
    });
  }

  setMix(master: number, music: number, sfx: number, muted: boolean) {
    const now = this.context.currentTime;
    this.master.gain.setTargetAtTime(muted ? 0 : master, now, 0.06);
    this.music.gain.setTargetAtTime(music * 0.26, now, 0.1);
    this.sfx.gain.setTargetAtTime(sfx * 0.72, now, 0.06);
  }

  setPhase(phase: GamePhase) {
    const roots: Record<GamePhase, [number, number, number, number]> = {
      title: [46.25, 69.3, 92.5, 420],
      briefing: [49, 73.42, 98, 500],
      investigation: [55, 82.41, 110, 620],
      trial: [58.27, 87.31, 116.54, 760],
      theory: [61.74, 92.5, 123.47, 680],
      reveal: [65.41, 98, 130.81, 940],
      aftermath: [49, 73.42, 98, 520],
    };
    const values = roots[phase];
    const now = this.context.currentTime;
    this.drones.forEach((oscillator, index) => oscillator.frequency.setTargetAtTime(values[index], now, 1.2));
    this.filter.frequency.setTargetAtTime(values[3], now, 0.8);
  }

  tone(frequency: number, duration = 0.2, volume = 0.18, type: OscillatorType = 'sine') {
    if (this.context.state !== 'running') return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.001, volume), now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain).connect(this.sfx);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.04);
  }

  noise(duration = 0.12, volume = 0.18, cutoff = 420) {
    if (this.context.state !== 'running') return;
    const length = Math.floor(this.context.sampleRate * duration);
    const buffer = this.context.createBuffer(1, length, this.context.sampleRate);
    const data = buffer.getChannelData(0);
    const sequence = this.noiseSequence++;
    for (let index = 0; index < length; index += 1) {
      const deterministic = Math.sin((index + 1) * 12.9898 + sequence * 78.233) * 43758.5453;
      const sample = (deterministic - Math.floor(deterministic)) * 2 - 1;
      data[index] = sample * (1 - index / length);
    }
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;
    gain.gain.value = volume;
    source.buffer = buffer;
    source.connect(filter).connect(gain).connect(this.sfx);
    source.start();
  }

  motif(midi: number[], volume = 0.04) {
    midi.slice(0, 3).forEach((note, index) => {
      const frequency = 440 * 2 ** ((note - 69) / 12);
      window.setTimeout(() => this.tone(frequency, 0.18, volume, 'sine'), index * 55);
    });
  }

  pulse(phase: GamePhase) {
    if (phase === 'investigation') this.tone(220, 0.55, 0.035, 'sine');
    if (phase === 'trial') this.tone(116.54, 0.18, 0.055, 'triangle');
    if (phase === 'theory') this.tone(246.94, 0.32, 0.045, 'sine');
    if (phase === 'reveal') {
      this.tone(130.81, 0.25, 0.07, 'triangle');
      this.tone(196, 0.36, 0.035, 'sine');
    }
  }

  effect(name: string) {
    switch (name) {
      case 'footstep':
        this.noise(0.07, 0.065, 230);
        break;
      case 'evidence':
        this.tone(659.25, 0.18, 0.12, 'sine');
        window.setTimeout(() => this.tone(987.77, 0.26, 0.09, 'sine'), 90);
        break;
      case 'wrong':
        this.tone(92.5, 0.42, 0.2, 'sawtooth');
        this.noise(0.18, 0.1, 180);
        break;
      case 'objection':
        this.tone(146.83, 0.2, 0.24, 'square');
        window.setTimeout(() => this.tone(293.66, 0.45, 0.18, 'triangle'), 80);
        break;
      case 'gavel':
        this.noise(0.15, 0.38, 520);
        this.tone(82.41, 0.5, 0.21, 'sine');
        break;
      case 'impact':
        this.noise(0.24, 0.34, 260);
        this.tone(55, 0.62, 0.28, 'sine');
        break;
      case 'murmur':
        this.noise(0.52, 0.075, 760);
        window.setTimeout(() => this.noise(0.38, 0.055, 540), 130);
        break;
      case 'reveal':
        [196, 246.94, 329.63].forEach((frequency, index) =>
          window.setTimeout(() => this.tone(frequency, 0.7, 0.12, 'sine'), index * 120),
        );
        break;
    }
  }

  destroy() {
    this.drones.forEach((oscillator) => {
      try { oscillator.stop(); } catch { /* already stopped */ }
    });
    if (this.context.state !== 'closed') void this.context.close().catch(() => undefined);
  }
}

function selectVoice(characterId: CharacterId): SpeechSynthesisVoice | null {
  const profile = characterById[characterId]?.voice;
  const voices = window.speechSynthesis?.getVoices() ?? [];
  if (!profile || voices.length === 0) return null;
  for (const preferred of profile.preferredNames) {
    const match = voices.find((voice) => voice.name.toLowerCase().includes(preferred.toLowerCase()));
    if (match) return match;
  }
  const english = voices.filter((voice) => voice.lang.toLowerCase().startsWith('en'));
  return english[characterId.length % Math.max(1, english.length)] ?? voices[0] ?? null;
}

export function AudioDirector() {
  const { state } = useGame();
  const score = useRef<ProceduralScore | null>(null);
  const spokenKey = useRef('');
  const handledAction = useRef(0);
  const [subtitle, setSubtitle] = useState<{ speaker: string; line: string } | null>(null);
  const eventBeat = useMemo(
    () => state.courtEvent ? getCourtEventBeat(state.courtEvent) : null,
    [state.courtEvent],
  );
  const eventId = state.courtEvent?.id;

  const unlock = useCallback(() => {
    if (!score.current || score.current.context.state === 'closed') score.current = new ProceduralScore();
    if (score.current.context.state === 'suspended') void score.current.context.resume();
    score.current.setMix(state.audio.master, state.audio.music, state.audio.sfx, state.muted);
    score.current.setPhase(state.phase);
  }, [state.audio.master, state.audio.music, state.audio.sfx, state.muted, state.phase]);

  const speak = useCallback((characterId: CharacterId, line: string, key: string) => {
    if (spokenKey.current === key || !('speechSynthesis' in window)) return;
    spokenKey.current = key;
    window.speechSynthesis.cancel();
    const character = characterById[characterId];
    if (!character) return;
    score.current?.motif(state.yuragi.agents[characterId].motif.midi, 0.035);
    const utterance = new SpeechSynthesisUtterance(line);
    utterance.voice = selectVoice(characterId);
    utterance.pitch = character.voice.pitch;
    utterance.rate = character.voice.rate;
    utterance.volume = state.muted ? 0 : state.audio.master * state.audio.voice;
    const signal = (speaking: boolean) => window.dispatchEvent(
      new CustomEvent('hotei:voice', { detail: { characterId, speaking } }),
    );
    utterance.onstart = () => {
      signal(true);
      setSubtitle({ speaker: character.name, line });
    };
    utterance.onend = () => {
      signal(false);
      window.setTimeout(() => setSubtitle(null), 650);
    };
    utterance.onerror = () => signal(false);
    window.speechSynthesis.speak(utterance);
  }, [state.audio.master, state.audio.voice, state.muted, state.yuragi.agents]);

  useEffect(() => {
    const onUnlock = () => unlock();
    window.addEventListener('pointerdown', onUnlock, { capture: true });
    window.addEventListener('keydown', onUnlock, { capture: true });
    return () => {
      window.removeEventListener('pointerdown', onUnlock, { capture: true });
      window.removeEventListener('keydown', onUnlock, { capture: true });
    };
  }, [unlock]);

  useEffect(() => () => {
    const current = score.current;
    score.current = null;
    current?.destroy();
    window.speechSynthesis?.cancel();
  }, []);

  useEffect(() => {
    score.current?.setMix(state.audio.master, state.audio.music, state.audio.sfx, state.muted);
  }, [state.audio, state.muted]);

  useEffect(() => {
    score.current?.setPhase(state.phase);
    const interval = window.setInterval(() => score.current?.pulse(state.phase), state.phase === 'trial' ? 1450 : 2200);
    return () => window.clearInterval(interval);
  }, [state.phase]);

  useEffect(() => {
    const onSfx = (event: Event) => {
      const name = (event as CustomEvent<{ name: string }>).detail?.name;
      if (name) score.current?.effect(name);
    };
    window.addEventListener('hotei:sfx', onSfx);
    return () => window.removeEventListener('hotei:sfx', onSfx);
  }, []);

  useEffect(() => {
    const action = state.actionLog.at(-1);
    if (!action || action.sequence === handledAction.current) return;
    handledAction.current = action.sequence;
    if (action.type === 'discover-evidence') score.current?.effect('evidence');
    if (action.type === 'wrong-presentation') score.current?.effect('wrong');
    if (action.type === 'expose-contradiction') score.current?.effect('objection');
    if (action.type === 'convene-tribunal') {
      score.current?.effect('gavel');
      const opening = CASE_001.statements[0];
      window.setTimeout(() => speak(opening.characterId, opening.text, `trial-opening-${action.sequence}`), 420);
    }
    if (action.type === 'submit-theory') score.current?.effect('reveal');

    const statement = action.target ? CASE_001.statements.find((item) => item.id === action.target) : undefined;
    if (action.type === 'navigate-statement' && statement) {
      speak(statement.characterId, statement.text, `statement-${statement.id}-${action.sequence}`);
    }
    if (action.type === 'press-statement' && statement) {
      const level = Math.max(1, Math.min(3, Number(action.value) || 1));
      speak(statement.characterId, statement.pressureLines[level - 1], `press-${statement.id}-${level}-${action.sequence}`);
    }
    if (action.type === 'question-character' && action.target) {
      const characterId = action.target as CharacterId;
      const first = CASE_001.statements.find((item) => item.characterId === characterId);
      if (first) speak(characterId, first.text, `question-${characterId}-${action.sequence}`);
    }
  }, [speak, state.actionLog]);

  useEffect(() => {
    if (!eventId || !eventBeat) return;
    const key = `event-${eventId}-${eventBeat.atMs}`;
    speak(eventBeat.speakerId, eventBeat.line, key);
    if (eventBeat.intensity === 'high') score.current?.effect('murmur');
    if (eventId === 'rook-lunge' && eventBeat.atMs === 3900) score.current?.effect('impact');
    if (eventBeat.speakerId === 'judge' && eventBeat.intensity === 'high') score.current?.effect('gavel');
  }, [eventBeat, eventId, speak]);

  if (!state.audio.subtitles || !subtitle || state.courtEvent) return null;
  return (
    <div className="voice-subtitle" aria-live="polite">
      <span>{subtitle.speaker}</span>
      <p>{subtitle.line}</p>
    </div>
  );
}
